/* eslint-disable react-hooks/exhaustive-deps */
import { IntegrationAppProvider } from "@membranehq/react";
import { useAuthInfo } from "@propelauth/react";
import { formatISO, fromUnixTime } from "date-fns";
import { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
// SpotlightSearch removed
import {
  useWorkspaceObjectSchema,
  useViews,
  useWorkspaceMembers,
  useWorkspacePreferences,
} from "../../hooks/QueryHooks";
import useSelectWorkspace from "../../hooks/useSelectWorkspace";
import { generateIntegrationAppToken } from "../../services/integrationAppEndpoint";
import { WorkspacePreferenceResponse } from "../../services/preference/preferenceEndpoints.interface";
import usePreferencesStore from "../../store/preference/preferencesStore";
import LeftSideBar from "./components/LeftSideBar";
import GlobalSpotlight from "../../shared/spotlight/GlobalSpotlight";
import { ReconnectBanner } from "../../components/banners/ReconnectBanner";
import { PendingApprovalsBadge } from "../../components/drawer/PendingApprovalsBadge";
import { PendingApprovalsDrawer } from "../../components/drawer/PendingApprovalsDrawer";
import { useDraftMentionStore } from "../../components/agno/messages/draftMentionStore";

const Homepage = () => {
  const [collapsed, setCollapsed] = useState(false); // Lifted state
  const drawerOpen = useDraftMentionStore((s) => s.isOpen);
  const focusDraftId = useDraftMentionStore((s) => s.focusDraftId);
  const closeDrawer = useDraftMentionStore((s) => s.closeDrawer);
  const authInfo: any = useAuthInfo();
  const { data, isLoading } = useSelectWorkspace();

  useWorkspaceMembers({
    workspaceId: data?.id,
    enabled: !!data?.id,
    retry: 2,
  });

  useWorkspaceObjectSchema({
    workspaceId: data?.id,
    enabled: !!data?.id,
  });

  // Fetch workspace preferences
  const preferences = useWorkspacePreferences({
    workspaceId: data?.id,
    enabled: !!data?.id,
  });

  // Store preferences in Zustand
  const setPreferences = usePreferencesStore(state => state.setPreferences);
  const setWorkspaceId = usePreferencesStore(state => state.setWorkspaceId);

  const views: any = useViews({
    workspaceId: data?.id,
    enabled: !!data?.id,
  });

  useEffect(() => {
    document.title = "Home | Hyperengage";
  }, []);

  useEffect(() => {
    if (data?.id) {
      setWorkspaceId(data.id);
    }

    // Check if we have valid preferences data and store it
    const preferencesData = preferences.data as WorkspacePreferenceResponse;

    if (preferencesData &&
      preferencesData.accounts &&
      preferencesData.users &&
      preferencesData.id) {
      setPreferences(preferencesData);
    }
  }, [data?.id, preferences.data, setPreferences, setWorkspaceId]);

  useEffect(() => {
    const selectedWorkspace: any =
      authInfo.orgHelper?.getOrg(data?.id) || undefined;
    if (
      import.meta.env.VITE_HE_WORKSPACE_KEY &&
      import.meta.env.VITE_HE_DATA_KEY &&
      selectedWorkspace?.orgId
    ) {
      window?.hyperengage("identify_account", {
        account_id: selectedWorkspace?.orgId,
        traits: {
          name: selectedWorkspace?.orgName,
          unique_identifier: selectedWorkspace?.orgMetadata?.unique_identifier,
          setup_complete: selectedWorkspace?.orgMetadata?.setup_complete,
          tracking_website_domain: data?.tracking_website_domain,
          workspace_default_currency: data?.workspace_default_currency,
          workspace_time_zone: data?.workspace_time_zone,
          avatar_url: data?.workspace_logo,
          mrr: 0,
          is_trial: true,
          created_at: data?.created_at,
        },
      });
      if (authInfo.user) {
        window?.hyperengage("identify_user", {
          user_id: authInfo.user.userId,
          traits: {
            created_at: formatISO(fromUnixTime(authInfo.user.createdAt)),
            name: `${authInfo.user.firstName} ${authInfo.user.lastName}`,
            first_name: authInfo.user.firstName,
            last_name: authInfo.user.lastName,
            email: authInfo.user.email,
            avatar_url: authInfo.user.pictureUrl,
            properties: authInfo.user?.properties,
            can_create_orgs: authInfo.user?.can_create_orgs,
            mfa_enabled: authInfo.user?.mfa_enabled,
          },
        });
      }
    }
  }, [data?.id]);

  if (!data?.id) {
    return <div>Loading...</div>;
  }

  // Dynamic token fetcher for IntegrationApp
  const fetchToken = async () => {
    if (data?.id) {
      try {
        // Generate workspace-level token by default
        // Member-level tokens will be handled by specific integration contexts
        const response = await generateIntegrationAppToken({
          workspace_id: data.id,
          isMemberLevel: false
        });
        return response?.data?.token;
      } catch (error) {
        console.error('Failed to fetch integration token:', error);
        return null;
      }
    }
    return null;
  };

  return (
    <IntegrationAppProvider fetchToken={fetchToken}>
      <div className="h-screen bg-background">
        <GlobalSpotlight />
        <div className="flex h-full overflow-hidden">
          {data?.id && (
            <LeftSideBar
              workspace={data}
              views={views?.data || [{ text: "empty", to: "/" }]}
              collapsed={collapsed}
              setCollapsed={setCollapsed}
            />
          )}

          <main className="relative flex-1 overflow-auto bg-background">
            <ReconnectBanner />
            {/* Pending approvals badge — floats at the top-right of the main panel */}
            <div className="absolute right-4 top-3 z-10">
              <PendingApprovalsBadge />
            </div>
            <PendingApprovalsDrawer
              open={drawerOpen}
              onClose={closeDrawer}
              focusDraftId={focusDraftId}
            />
            {(views.isLoading || isLoading || preferences.isLoading) && (
              <div className="absolute inset-0 z-30 flex items-center justify-center bg-background/70 backdrop-blur-[1px]">
                <span className="h-7 w-7 animate-spin rounded-full border-2 border-primary/30 border-t-primary" />
              </div>
            )}
            {views?.data?.length && (
              <Outlet />
            )}
          </main>
        </div>
      </div>
    </IntegrationAppProvider>
  );
};

export default Homepage;
